import { useEffect, useRef, useState } from "react";
import { socket } from "../App";
import { ORDER_BOOK_LENGTH, SOCKET, socketPairTopicPrefix } from "../constants/constants";
import { registerExchangePairPostJoin } from "../services/exchangePairRoomSocket";
import { useDispatch, useSelector } from "react-redux";
import { ATS, setBuyOrderBook, setLastPrice, setSellOrderBook, setTradeBook } from "../features/ats/atsSlice";
import { parseJSON, toFix } from "../helpers/commonHelper";
import * as _ from 'lodash';

// lodash orderBy('price') is lexicographic ("10" < "9"); sort bids/offers by numeric price level.
const numericPrice = (row) => Number(row?.price);

const sortOrderBookRows = (rows) => _.orderBy(rows, [numericPrice], ["desc"]);

const samePriceLevel = (a, b) => {
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && Math.abs(na - nb) < 1e-10;
};

function totalDecimalPlaces(priceDecimal, quantityDecimal) {
    const pd = Number(priceDecimal);
    const qd = Number(quantityDecimal);
    const eff = (d) => (Number.isFinite(d) && d > 0 ? Math.min(Math.floor(d), 12) : 4);
    return Math.min(eff(pd) + eff(qd), 16);
}

const QTY_EPS = 1e-9;

const isQtyZero = (qty) => !Number.isFinite(Number(qty)) || Number(qty) <= QTY_EPS;

export const OrderBookHook = () => {
    const dispatch = useDispatch();
    const buyOrderBook = useSelector(state => state?.ats?.buyOrderBook);
    const sellOrderBook = useSelector(state => state?.ats?.sellOrderBook);
    const selectedPair = useSelector(state => state?.ats?.selectedPair);
    const lastTrade = useSelector(state => state?.ats?.lastTrade);
    const ordersLimit = ORDER_BOOK_LENGTH;
    const buyRef = useRef(buyOrderBook)
    buyRef.current = buyOrderBook
    const sellRef = useRef(sellOrderBook);
    sellRef.current = sellOrderBook;
    const orderBookRefreshTimerRef = useRef(null);

    const fetchOrderBooks = () => {
        if (!selectedPair?.pair_key) return Promise.resolve();
        return dispatch(ATS.fetchBuySellOrderBook({
            pairName: selectedPair.pair_key,
            limit: ordersLimit,
        }));
    };

    // Reconcile both bid/ask books from API after trades (keeps remaining qty on book, no wrongful clear).
    useEffect(() => {
        if (!lastTrade || !selectedPair?.pair_key) return undefined;
        if (orderBookRefreshTimerRef.current) clearTimeout(orderBookRefreshTimerRef.current);
        orderBookRefreshTimerRef.current = setTimeout(() => {
            fetchOrderBooks();
        }, 200);
        return () => {
            if (orderBookRefreshTimerRef.current) clearTimeout(orderBookRefreshTimerRef.current);
        };
    }, [lastTrade, selectedPair?.pair_key]);

    useEffect(() => {
        if (!selectedPair?.pair_key) return;

        const room = socketPairTopicPrefix(selectedPair.pair_key);
        if (!room) return;

        const evBuy = `${room}${SOCKET.BUY_ORDERBOOK}`;
        const evSell = `${room}${SOCKET.SELL_ORDERBOOK}`;
        const evCancel = `${room}${SOCKET.CANCEL_ORDER}`;
        const evSbTrade = `${room}${SOCKET.SPEEDBUMP_TRADE_BOOK}`;

        const attachOrderBookSocket = () => {
            socket.off(evBuy);
            socket.off(evSell);
            socket.off(evCancel);
            socket.off(evSbTrade);
            socket.on(evBuy, async (order) => {
                console.log('BUY ORDERBOOK -> ', order);
                if (!order || typeof order !== "object") return;
                const buyOrders = orderBookManagement(
                    [...buyRef.current],
                    order,
                    "add",
                    selectedPair.price_decimal,
                    selectedPair.quantity_decimal,
                    0,
                );
                dispatch(setBuyOrderBook(parseJSON(buyOrders)));
            });
            socket.on(evSell, async (order) => {
                if (!order || typeof order !== "object") return;
                const sellOrders = orderBookManagement(
                    [...sellRef.current],
                    order,
                    "add",
                    selectedPair.price_decimal,
                    selectedPair.quantity_decimal,
                    1,
                );
                dispatch(setSellOrderBook(parseJSON(sellOrders)));
            });
            socket.on(evCancel, async (order) => {
                if (!order || typeof order !== "object") return;
                const orderSide = Number(order.side);
                const isSell = orderSide === 1;
                const targetBook = parseJSON(isSell ? [...sellRef.current] : [...buyRef.current]);
                if (isSell) order.sellerPrice = order.price;
                else order.buyerPrice = order.price;
                const book = orderBookManagement(
                    [...targetBook],
                    order,
                    "removeCancel",
                    selectedPair.price_decimal,
                    selectedPair.quantity_decimal,
                    orderSide,
                );
                if (isSell) dispatch(setSellOrderBook(parseJSON(book)));
                else dispatch(setBuyOrderBook(parseJSON(book)));
                fetchOrderBooks();
            });
            socket.on(evSbTrade, () => {
                fetchOrderBooks();
            });
            console.log("[OrderBookHook] listening", {
                pair_key: selectedPair.pair_key,
                prefix: room,
                events: [evBuy, evSell, evCancel, evSbTrade],
            });
        };

        fetchOrderBooks();
        attachOrderBookSocket();

        /** After reconnect + joinRoom (central), snap REST order book to server truth. */
        const unregisterPostJoin = registerExchangePairPostJoin(() => {
            fetchOrderBooks();
        });

        const onOrderBooksRefresh = (event) => {
            const pairName = event?.detail?.pairName || selectedPair?.pair_key;
            if (!pairName || pairName !== selectedPair?.pair_key) return;
            fetchOrderBooks();
        };
        window.addEventListener("order-books:refresh", onOrderBooksRefresh);

        return () => {
            window.removeEventListener("order-books:refresh", onOrderBooksRefresh);
            unregisterPostJoin();
            socket.off(evBuy);
            socket.off(evSell);
            socket.off(evCancel);
            socket.off(evSbTrade);
        };
    }, [dispatch, ordersLimit, selectedPair?.pair_key, selectedPair]);
    return {
        buyOrderBook: parseJSON(buyOrderBook),
        sellOrderBook: parseJSON(sellOrderBook)
    };
};
/** Initial / step size for REST market-trade pagination (`limit`). */
export const MARKET_TRADES_PAGE_SIZE = 20;

export const TradeBookHook = () => {
    const dispatch = useDispatch();
    const selectedPair = useSelector((state) => state?.ats?.selectedPair);
    const tradeBook = useSelector((state) => state?.ats?.tradeBook);
    const lastTrade = useSelector((state) => state?.ats?.lastTrade);
    const [tradeFetchLimit, setTradeFetchLimit] = useState(MARKET_TRADES_PAGE_SIZE);
    /** Any REST trade-book request in flight (disables Load More button). */
    const [tradeBookFetchBusy, setTradeBookFetchBusy] = useState(false);
    const [marketTradesHasMore, setMarketTradesHasMore] = useState(false);
    /** Loading label + spinner only after user taps Load More. */
    const [marketTradesLoadMoreBusy, setMarketTradesLoadMoreBusy] = useState(false);

    const tradeRef = useRef(tradeBook);
    tradeRef.current = tradeBook;

    useEffect(() => {
        setTradeFetchLimit(MARKET_TRADES_PAGE_SIZE);
        setMarketTradesLoadMoreBusy(false);
    }, [selectedPair?.pair_key]);

    useEffect(() => {
        if (!selectedPair?.pair_key) return undefined;

        let cancelled = false;
        (async () => {
            setTradeBookFetchBusy(true);
            try {
                const response = await dispatch(
                    ATS.fetchMarketTrade({
                        pairName: selectedPair.pair_key,
                        limit: tradeFetchLimit,
                    }),
                );
                if (cancelled) return;
                if (response?.status === 200) {
                    const list = response.data?.tradeList ?? [];
                    dispatch(setLastPrice(list.length ? list[0].price : 0));
                    setMarketTradesHasMore(list.length >= tradeFetchLimit);
                } else {
                    setMarketTradesHasMore(false);
                }
            } finally {
                if (!cancelled) {
                    setTradeBookFetchBusy(false);
                    setMarketTradesLoadMoreBusy(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch, selectedPair?.pair_key, tradeFetchLimit]);

    const loadMoreMarketTrades = () => {
        setMarketTradesLoadMoreBusy(true);
        setTradeFetchLimit((n) => n + MARKET_TRADES_PAGE_SIZE);
    };

    const handleTrade = (trade) => {
        if (!trade) return;
        trade.quantity = trade.filled_amount;
        const capFromState =
            tradeRef.current?.length > 0
                ? tradeRef.current.length
                : MARKET_TRADES_PAGE_SIZE;
        const cap = Math.max(capFromState, MARKET_TRADES_PAGE_SIZE);
        const trades = manageTradeBook([...tradeRef.current], trade, cap);
        dispatch(setTradeBook(trades));
    };

    useEffect(() => {
        handleTrade(parseJSON(lastTrade));
    }, [lastTrade]);

    return {
        tradeBook,
        marketTradesFetching: tradeBookFetchBusy,
        marketTradesLoadMoreFetching: marketTradesLoadMoreBusy,
        marketTradesHasMore,
        loadMoreMarketTrades,
    };
};

const orderBookManagement = (orderBook, order, operation, priceDecimal, quantityDecimal, side) => {
    try {
        if (operation === 'add') {
            const incomingPrice = order?.price;
            const orderIndex = orderBook.findIndex((o) => samePriceLevel(o.price, incomingPrice));
            if (orderIndex < 0) {
                orderBook.push(parseJSON(order));
                orderBook = sortOrderBookRows(orderBook);
                if (orderBook.length > ORDER_BOOK_LENGTH) orderBook.splice(side === 0 ? (orderBook.length - 1) : 0, 1);
                return orderBook;
            }
            const currentQty = Number(orderBook[orderIndex].quantity) + Number(order.quantity);
            const totalDec = totalDecimalPlaces(priceDecimal, quantityDecimal);
            const px = Number(orderBook[orderIndex].price);
            orderBook[orderIndex] = parseJSON({
                price: orderBook[orderIndex].price,
                quantity: toFix(currentQty, quantityDecimal),
                total: toFix(px * currentQty, totalDec),
            });
            orderBook = sortOrderBookRows(orderBook);
            return orderBook;
        }
        if (operation === 'removeTrade' || operation === 'removeCancel') {
            const refPrice = side === 0 ? order.buyerPrice : order.sellerPrice;
            const orderIndex = orderBook.findIndex((o) => samePriceLevel(o.price, refPrice));
            if (orderIndex < 0) return orderBook;

            const bookQty = Number(orderBook[orderIndex].quantity);
            const orderQty = Number(order.quantity) || 0;
            let removeQty = 0;

            if (operation === 'removeTrade') {
                removeQty = Math.min(bookQty, orderQty);
            } else {
                const filled = Number(order.filled_amount) || 0;
                const remainingOnOrder =
                    order.remaining_quantity != null
                        ? Number(order.remaining_quantity)
                        : Math.max(0, orderQty - filled);
                if (isQtyZero(remainingOnOrder)) return orderBook;
                removeQty = Math.min(bookQty, remainingOnOrder);
            }

            if (isQtyZero(removeQty)) return orderBook;

            const newQty = bookQty - removeQty;
            if (isQtyZero(newQty) || newQty < 0) {
                orderBook.splice(orderIndex, 1);
                return orderBook;
            }

            const totalDec = totalDecimalPlaces(priceDecimal, quantityDecimal);
            const px = Number(orderBook[orderIndex].price);
            orderBook[orderIndex] = parseJSON({
                price: orderBook[orderIndex].price,
                quantity: toFix(newQty, quantityDecimal),
                total: toFix(px * newQty, totalDec),
            });
            return orderBook;
        }
    } catch (error) {
        console.log('OrderBook Management Issue -> ', error);
    }
    return orderBook;
};
const manageTradeBook = (tradeBook, trade, maxRows = MARKET_TRADES_PAGE_SIZE) => {
    const cap = Number(maxRows);
    const limit = Number.isFinite(cap) && cap > 0 ? cap : MARKET_TRADES_PAGE_SIZE;
    try {
        tradeBook.unshift({
            total: trade.total,
            price: trade.price,
            filled_amount: trade.filled_amount,
            created_at: trade.created_at,
        });
        while (tradeBook.length > limit) {
            tradeBook.splice(tradeBook.length - 1, 1);
        }
        return tradeBook;
    } catch (error) {
        console.log("Error in Manage Trade - ", error);
        return tradeBook;
    }
};